import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FaSave, FaClock, FaExclamationTriangle } from "react-icons/fa";
import { getSchoolTimings, saveSchoolTimings } from "../../services/schoolService";
import "../teacher/TeacherLayer.css";

const ErrorToast = ({ message }) => {
  const displayMsg = typeof message === "string" 
    ? message 
    : (message && typeof message === "object" ? Object.values(message).flat().join(", ") : JSON.stringify(message) || "Something went wrong");
    
  return (
    <div className="d-flex align-items-start gap-2">
      <div className="text-danger mt-1 fs-5">
        <FaExclamationTriangle />
      </div>
      <div>
        <div className="fw-bold text-danger mb-1">Action Failed</div>
        <div style={{ fontSize: '13.5px', color: '#444' }}>{displayMsg}</div>
      </div>
    </div>
  );
};

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

// Helper to format API time (e.g. "08:00 AM" or "08:00:00") to HTML input time ("HH:mm")
const parseTimeToInput = (timeStr) => {
  if (!timeStr) return "";
  if (timeStr.includes("AM") || timeStr.includes("PM")) {
    const [time, modifier] = timeStr.split(" ");
    let [hours, minutes] = time.split(":");
    if (hours === "12") hours = "00";
    if (modifier === "PM") hours = parseInt(hours, 10) + 12;
    hours = hours.toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  }
  return timeStr.slice(0, 5);
};

// Helper to format HTML input time ("HH:mm") to API payload ("HH:mm:00")
const formatTimeForApi = (timeStr) => {
  if (!timeStr) return "";
  if (timeStr.split(":").length === 2) {
    return `${timeStr}:00`;
  }
  return timeStr;
};

const SchoolTimingsLayer = () => {
  const [loading, setLoading] = useState(false);
  const [timingsList, setTimingsList] = useState([]);
  const [form, setForm] = useState({
    id: null,
    day_name: "Monday",
    batch_name: "Morning Batch",
    school_start_time: "",
    late_after: "",
    half_day_before: "",
    school_end_time: "",
    status: "active",
  });

  useEffect(() => {
    fetchTimings();
  }, []);

  const fetchTimings = async () => {
    try {
      setLoading(true);
      const res = await getSchoolTimings();
      const rawList = res?.timings || res?.data?.timings || (Array.isArray(res?.data) ? res.data : []);
      if (Array.isArray(rawList)) {
        setTimingsList(rawList);
      } else {
        setTimingsList([]);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEdit = (item) => {
    setForm({
      id: item.id || null,
      day_name: item.day_name || "Monday",
      batch_name: item.batch_name || "Morning Batch",
      school_start_time: parseTimeToInput(item.school_start_time),
      late_after: parseTimeToInput(item.late_after),
      half_day_before: parseTimeToInput(item.half_day_before),
      school_end_time: parseTimeToInput(item.school_end_time),
      status: item.status || "active",
    });
  };

  const resetForm = () => {
    setForm({
      id: null,
      day_name: "Monday",
      batch_name: "Morning Batch",
      school_start_time: "",
      late_after: "",
      half_day_before: "",
      school_end_time: "",
      status: "active",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const timingObj = {
        ...(form.id ? { id: form.id } : {}),
        day_name: form.day_name,
        batch_name: form.batch_name,
        school_start_time: formatTimeForApi(form.school_start_time),
        late_after: formatTimeForApi(form.late_after),
        half_day_before: formatTimeForApi(form.half_day_before),
        school_end_time: formatTimeForApi(form.school_end_time),
        status: form.status,
      };

      const payload = {
        timings: [timingObj]
      };

      const res = await saveSchoolTimings(payload);
      const isSuccess = res?.status === true || res?.status === "true" || res?.status === 1 || res?.status === "1" || res?.status === "success";

      if (isSuccess) {
        toast.success(res?.message || "School timings updated successfully");
        resetForm();
        fetchTimings();
      } else {
        const message = res?.message || "Failed to update school timings";
        toast.error(<ErrorToast message={message} />, { icon: false });
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error.message || "Server Error";
      toast.error(<ErrorToast message={errorMsg} />, { icon: false });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="school-wrapper">
      <div className="school-card smooth-card">
        <div className="admin-page-shell">
          <div className="admin-page-header list-header">
            <div>
              <h5 className="admin-page-title d-flex align-items-center gap-2">
                <FaClock className="text-primary" /> School Timings
              </h5>
              <p className="text-secondary-light m-0 mt-1" style={{ fontSize: "14px" }}>
                Add or edit timings for default operations and specific single days or batches.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="mt-4">
            <h6 className="mb-3 text-primary border-bottom pb-2">
              {form.id ? "Edit Timing" : "Configure Day & Batch Timing"}
            </h6>
            <div className="row">
              <div className="col-lg-2 col-md-4 mb-3">
                <label className="form-label">Day Name</label>
                <select className="form-select" name="day_name" value={form.day_name} onChange={handleChange}>
                  <option value="default">Default</option>
                  {DAYS_OF_WEEK.map(day => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>

              <div className="col-lg-2 col-md-4 mb-3">
                <label className="form-label">Batch Name</label>
                <input
                  type="text"
                  name="batch_name"
                  className="form-control"
                  placeholder="e.g. Morning Batch"
                  value={form.batch_name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-lg-2 col-md-4 mb-3">
                <label className="form-label">Start Time</label>
                <input type="time" name="school_start_time" className="form-control" value={form.school_start_time} onChange={handleChange} required />
              </div>

              <div className="col-lg-2 col-md-4 mb-3">
                <label className="form-label">Late After</label>
                <input type="time" name="late_after" className="form-control" value={form.late_after} onChange={handleChange} required />
              </div>

              <div className="col-lg-2 col-md-4 mb-3">
                <label className="form-label">Half Day Before</label>
                <input type="time" name="half_day_before" className="form-control" value={form.half_day_before} onChange={handleChange} required />
              </div>

              <div className="col-lg-2 col-md-4 mb-3">
                <label className="form-label">End Time</label>
                <input type="time" name="school_end_time" className="form-control" value={form.school_end_time} onChange={handleChange} required />
              </div>

              <div className="col-lg-2 col-md-4 mb-3">
                <label className="form-label">Status</label>
                <select name="status" className="form-select" value={form.status} onChange={handleChange}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="d-flex flex-column flex-sm-row gap-2 mt-3 justify-content-center justify-content-sm-start">
              <button type="submit" className="btn btn-primary d-flex align-items-center justify-content-center gap-2 px-4 py-2" disabled={loading}>
                <FaSave /> {loading ? "Saving..." : form.id ? "Update Timing" : "Save Timing"}
              </button>
              {form.id && (
                <button type="button" className="btn btn-outline-secondary px-4 py-2" onClick={resetForm}>
                  Cancel Edit
                </button>
              )}
            </div>
          </form>

          <div className="mt-5">
            <h6 className="mb-3 text-primary border-bottom pb-2">Configured Timings List</h6>
            <div className="table-responsive">
              <table className="table table-bordered table-striped align-middle">
                <thead className="bg-light text-center text-nowrap">
                  <tr>
                    <th>ID</th>
                    <th>Day Name</th>
                    <th>Batch Name</th>
                    <th>Start Time</th>
                    <th>Late After</th>
                    <th>Half Day</th>
                    <th>End Time</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody className="text-center text-nowrap">
                  {timingsList.length > 0 ? (
                    timingsList.map((item, index) => (
                      <tr key={item.id || index}>
                        <td>{item.id || index + 1}</td>
                        <td>
                          {item.day_name === "default" ? (
                            <span className="badge bg-primary">Default</span>
                          ) : (
                            <span className="fw-bold">{item.day_name}</span>
                          )}
                        </td>
                        <td>
                          <span className="badge bg-info bg-opacity-10 text-info border border-info">
                            {item.batch_name || "N/A"}
                          </span>
                        </td>
                        <td>{item.school_start_time}</td>
                        <td>{item.late_after}</td>
                        <td>{item.half_day_before}</td>
                        <td>{item.school_end_time}</td>
                        <td>
                          <span className={`badge px-3 py-2 ${item.status === 'active' ? 'bg-success bg-opacity-10 text-success border border-success' : 'bg-danger bg-opacity-10 text-danger border border-danger'}`}>
                            {item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : "Active"}
                          </span>
                        </td>
                        <td>
                          <button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(item)}>
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-3 text-muted">No timings configured yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SchoolTimingsLayer;
